import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react"

import { Tooltip } from "~/components/reusable/Tooltip"
import { withTypographicMinus } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { figureToneClass, type FigureTone } from "~/utils/figureTone"

export type ChartReadoutMetric = {
  label: string
  value: ReactNode
  /** Colours the value; only for a difference (Δ vs LY), never a total. */
  tone?: FigureTone
  /** Minimum slot width in px: the widest usual value, so hover doesn't shift the strip. */
  width?: number
  /** Once every `dropOrder` metric is gone: value only, label in a tooltip. */
  collapsible?: boolean
  /** Minimum slot width in px while collapsed: the widest value alone. */
  compactWidth?: number
  /** Drops before any label collapses, lowest first; equal values drop together. Never on totals. */
  dropOrder?: number
}

/** Widths of one metric slot in px; the `tight` ones ignore its min width. */
export type ChartReadoutSlotSize = {
  full: number
  compact: number
  tightFull: number
  tightValue: number
  collapsible: boolean
  dropOrder?: number
}

/**
 * A fit step: `collapse` hides collapsible labels, `dropThrough` hides metrics whose `dropOrder`
 * is at most this (0 = none), `tight` drops min widths, `collapseAll` hides every label and `keep`
 * caps the shown metrics from the left. `fits` is false only when the first metric alone overflows.
 */
export type ChartReadoutFit = {
  collapse: boolean
  dropThrough: number
  tight: boolean
  collapseAll: boolean
  keep: number
  fits: boolean
}

type FitStep = Omit<ChartReadoutFit, "fits">

const FULL_FIT: ChartReadoutFit = {
  collapse: false,
  dropThrough: 0,
  tight: false,
  collapseAll: false,
  keep: Infinity,
  fits: true,
}

const isDropped = (dropOrder: number | undefined, dropThrough: number) =>
  dropOrder != null && dropOrder <= dropThrough

const isCollapsed = (step: FitStep, collapsible: boolean | undefined) =>
  step.collapseAll || (step.collapse && !!collapsible)

/**
 * Steps widest first: everything; each `dropOrder`, cumulative, with every label kept; collapsible
 * labels hidden; min widths off; every label off; then the rightmost metrics one at a time, down to
 * the first. A label only goes once every droppable metric is gone: an unlabelled value reads worse
 * than a missing metric the page shows elsewhere.
 */
function fitSteps(slots: ChartReadoutSlotSize[]): FitStep[] {
  const collapse = slots.some((slot) => slot.collapsible)
  const dropOrders = [
    ...new Set(slots.flatMap((slot) => (slot.dropOrder == null ? [] : [slot.dropOrder]))),
  ].sort((a, b) => a - b)
  const dropThrough = dropOrders.at(-1) ?? 0
  const base = { tight: false, collapseAll: false, keep: Infinity }
  const remaining = slots.filter((slot) => !isDropped(slot.dropOrder, dropThrough)).length
  const last = { collapse, dropThrough, tight: true, collapseAll: true }
  return [
    { ...base, collapse: false, dropThrough: 0 },
    ...dropOrders.map((order) => ({ ...base, collapse: false, dropThrough: order })),
    ...(collapse ? [{ ...base, collapse: true, dropThrough }] : []),
    { ...base, collapse, dropThrough, tight: true },
    { ...last, keep: Infinity },
    ...Array.from({ length: Math.max(0, remaining - 1) }, (_, i) => ({
      ...last,
      keep: remaining - 1 - i,
    })),
  ]
}

function shownIndexes(slots: { dropOrder?: number }[], step: FitStep): Set<number> {
  const shown = new Set<number>()
  slots.forEach((slot, i) => {
    if (!isDropped(slot.dropOrder, step.dropThrough) && shown.size < step.keep) shown.add(i)
  })
  return shown
}

function slotWidth(slot: ChartReadoutSlotSize, step: FitStep) {
  const collapsed = isCollapsed(step, slot.collapsible)
  if (step.tight) return collapsed ? slot.tightValue : slot.tightFull
  return collapsed ? slot.compact : slot.full
}

/**
 * The widest step that fits in `available` px, with `gap` before every slot. `scope` is the scope
 * slot at its min width, `tightScope` without it.
 */
export function fitChartReadout({
  available,
  gap,
  scope,
  tightScope = scope,
  slots,
}: {
  available: number
  gap: number
  scope: number
  tightScope?: number
  slots: ChartReadoutSlotSize[]
}): ChartReadoutFit {
  const steps = fitSteps(slots)
  const widthAt = (step: FitStep) => {
    const shown = shownIndexes(slots, step)
    return slots.reduce(
      (total, slot, i) => (shown.has(i) ? total + gap + slotWidth(slot, step) : total),
      step.tight ? tightScope : scope,
    )
  }
  const fitting = steps.find((step) => widthAt(step) <= available)
  return fitting ? { ...fitting, fits: true } : { ...steps[steps.length - 1]!, fits: false }
}

const minWidth = (width: number | undefined): CSSProperties | undefined =>
  width == null ? undefined : { minWidth: width }

const SLOT_CLASS = "flex shrink-0 items-center gap-1.5 whitespace-nowrap"
const SCOPE_CLASS = "label-sm text-fg-base whitespace-nowrap"

const SlotBody = ({ metric, collapsed }: { metric: ChartReadoutMetric; collapsed: boolean }) => (
  <>
    <span className={cn("paragraph-sm text-fg-muted", collapsed && "sr-only")}>{metric.label}</span>
    <span
      className={cn(
        "label-sm tabular-nums",
        figureToneClass(metric.tone ?? "neutral", "text-fg-base"),
      )}
    >
      {withTypographicMinus(metric.value)}
    </span>
  </>
)

const slotStyle = (metric: ChartReadoutMetric, collapsed: boolean, tight: boolean) => {
  if (tight) return undefined
  return minWidth(collapsed ? (metric.compactWidth ?? metric.width) : metric.width)
}

const MetricSlot = ({
  metric,
  collapsed,
  tight,
}: {
  metric: ChartReadoutMetric
  collapsed: boolean
  tight: boolean
}) => (
  <Tooltip title={metric.label} disabled={!collapsed}>
    <span className={SLOT_CLASS} style={slotStyle(metric, collapsed, tight)}>
      <SlotBody metric={metric} collapsed={collapsed} />
    </span>
  </Tooltip>
)

const px = (value: string) => Number.parseFloat(value) || 0

const sameFit = (a: ChartReadoutFit, b: ChartReadoutFit) =>
  (Object.keys(a) as (keyof ChartReadoutFit)[]).every((key) => a[key] === b[key])

/**
 * Measures each slot's natural width, with and without its label, in a hidden copy of the strip
 * and picks the widest fitting step on each render and resize, before paint, so the strip never
 * shows a clipped frame. A min width only ever widens a slot, so it is applied arithmetically.
 */
function useReadoutFit(metrics: ChartReadoutMetric[], scopeWidth: number) {
  const stripRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef({ metrics, scopeWidth })
  const [fit, setFit] = useState<ChartReadoutFit>(FULL_FIT)

  const measure = useCallback(() => {
    const strip = stripRef.current
    const measurer = measureRef.current
    if (!strip || !measurer) return
    const widthOf = (key: string) =>
      measurer.querySelector(`[data-fit="${key}"]`)?.getBoundingClientRect().width ?? 0
    const style = getComputedStyle(strip)
    const { metrics, scopeWidth } = inputRef.current
    const tightScope = widthOf("scope")
    const next = fitChartReadout({
      available: strip.clientWidth - px(style.paddingLeft) - px(style.paddingRight),
      gap: px(style.columnGap),
      scope: Math.max(scopeWidth, tightScope),
      tightScope,
      slots: metrics.map((metric, i) => {
        const tightFull = widthOf(`full-${i}`)
        const tightValue = widthOf(`value-${i}`)
        return {
          full: Math.max(metric.width ?? 0, tightFull),
          compact: Math.max(metric.compactWidth ?? metric.width ?? 0, tightValue),
          tightFull,
          tightValue,
          collapsible: !!metric.collapsible,
          dropOrder: metric.dropOrder,
        }
      }),
    })
    setFit((current) => (sameFit(current, next) ? current : next))
  }, [])

  useLayoutEffect(() => {
    inputRef.current = { metrics, scopeWidth }
    measure()
  })

  useLayoutEffect(() => {
    const strip = stripRef.current
    const measurer = measureRef.current
    if (!strip || !measurer) return
    const observer = new ResizeObserver(() => measure())
    observer.observe(strip)
    observer.observe(measurer)
    return () => observer.disconnect()
  }, [measure])

  return { stripRef, measureRef, fit }
}

/**
 * Strip between a chart's toolbar and its plot: a scope label plus metric slots, showing the
 * scope's totals at rest and the hovered or keyboard-focused period otherwise. Never wraps or
 * clips: it collapses `collapsible` labels, then drops metrics by `dropOrder`, until it fits; the
 * scope truncates only when the first metric alone overflows.
 * Pass it to SuperChart's `readout` with {@link useChartReadout}, and turn the chart tooltip off.
 * Canonical intent: {@link ../../design-system/intents.ts CHART_READOUT_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const ChartReadout = ({
  scope,
  scopeWidth = 150,
  metrics,
  className,
}: {
  /** "Season HS27" at rest, "W23 · 7–13 Jun 2027" for the active period. */
  scope: ReactNode
  /** Minimum px width of the scope slot; size it for the usual period label. */
  scopeWidth?: number
  metrics: ChartReadoutMetric[]
  className?: string
}) => {
  const { stripRef, measureRef, fit } = useReadoutFit(metrics, scopeWidth)
  if (metrics.length === 0) return null
  const shown = shownIndexes(metrics, fit)
  return (
    <div
      ref={stripRef}
      aria-live="polite"
      className={cn(
        "relative flex flex-nowrap items-center gap-5 overflow-hidden rounded-[8px] bg-subtle px-3 py-2",
        className,
      )}
    >
      <Tooltip title={typeof scope === "string" ? scope : undefined} disabled={fit.fits}>
        <span
          className={cn(SCOPE_CLASS, fit.fits ? "shrink-0" : "min-w-0 truncate")}
          style={fit.tight ? undefined : minWidth(scopeWidth)}
        >
          {scope}
        </span>
      </Tooltip>
      {metrics.map((metric, i) =>
        !shown.has(i) ? null : (
          <MetricSlot
            key={i}
            metric={metric}
            collapsed={isCollapsed(fit, metric.collapsible)}
            tight={fit.tight}
          />
        ),
      )}
      {/* Each slot at its natural width, out of flow, so the fit is chosen before paint. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max"
      >
        <span data-fit="scope" className={cn(SCOPE_CLASS, "shrink-0")}>
          {scope}
        </span>
        {metrics.map((metric, i) => (
          <Fragment key={i}>
            <span data-fit={`full-${i}`} className={SLOT_CLASS}>
              <SlotBody metric={metric} collapsed={false} />
            </span>
            <span data-fit={`value-${i}`} className={SLOT_CLASS}>
              <SlotBody metric={metric} collapsed />
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/**
 * Active-period state for a {@link ChartReadout}. Pass `plotProps`, `onPointHover` and
 * `highlightIndex` to SuperChart: the plot takes keyboard focus (←/→ step, Home/End jump, Esc
 * clears). `activeIndex` is the period to show, or `null` for the totals.
 */
export function useChartReadout({ count, label }: { count: number; label: string }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [keyboardIndex, setKeyboardIndex] = useState<number | null>(null)

  const onPointHover = useCallback((index: number | null) => {
    setKeyboardIndex(null)
    setHoverIndex(index)
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (count === 0) return
    const last = count - 1
    const current = keyboardIndex ?? hoverIndex
    const next = nextIndex(event.key, current, last)
    if (next === undefined) return
    event.preventDefault()
    setHoverIndex(null)
    setKeyboardIndex(next)
  }

  const activeIndex = keyboardIndex ?? hoverIndex
  return {
    activeIndex: activeIndex != null && activeIndex < count ? activeIndex : null,
    onPointHover,
    /** Only the keyboard's index: the pointer already highlights what it hovers. */
    highlightIndex: keyboardIndex,
    plotProps: {
      tabIndex: 0,
      role: "group",
      "aria-label": `${label}. Use the left and right arrow keys to step through the periods.`,
      onKeyDown,
      onBlur: () => setKeyboardIndex(null),
      className: "rounded-[8px] focus-visible:outline-none focus-visible:shadow-borders-focus",
    },
  }
}

// `undefined` = not a key the readout handles.
function nextIndex(key: string, current: number | null, last: number): number | null | undefined {
  switch (key) {
    case "ArrowRight":
      return current == null ? 0 : Math.min(current + 1, last)
    case "ArrowLeft":
      return current == null ? last : Math.max(current - 1, 0)
    case "Home":
      return 0
    case "End":
      return last
    case "Escape":
      return null
    default:
      return undefined
  }
}
